import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { storageService } from "@/services/storageService";
import { IMAGE_UPLOAD_MAX_MB } from "@/lib/compressImage";

export default function GalleryUploadField({
  label = "Gallery",
  value = [],
  onChange,
  nameHint = "event",
  testId,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const images = Array.isArray(value) ? value : [];

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith("image/"));
    if (!files.length) return;

    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map((file) => storageService.uploadEventGalleryImage(file, nameHint))
      );
      onChange([...images, ...uploaded]);
      toast.success(files.length === 1 ? "Image uploaded" : `${files.length} images uploaded`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function onInputChange(e) {
    handleFiles(e.target.files);
    e.target.value = "";
  }

  async function removeAt(index) {
    const url = images[index];
    if (!url) return;

    setUploading(true);
    try {
      if (storageService.isManagedR2Url(url)) {
        await storageService.deleteR2Url(url);
      }
      onChange(images.filter((_, i) => i !== index));
      toast.success("Image removed");
    } catch (err) {
      toast.error(err.message || "Could not delete image from storage");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div data-testid={testId}>
      <label className="label-bcs">{label}</label>

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {images.map((url, i) => (
            <div key={`${url}-${i}`} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-cream">
              <img src={url} alt="" className="w-full h-28 object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={uploading}
                className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 text-ink-muted hover:text-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                aria-label={`Remove image ${i + 1}`}
                data-testid={testId ? `${testId}-remove-${i}` : undefined}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={onInputChange}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-ink hover:bg-cream transition-colors disabled:opacity-60"
          data-testid={testId ? `${testId}-btn` : undefined}
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {uploading ? "Uploading…" : images.length ? "Add more images" : "Upload images"}
        </button>
      </div>

      <p className="mt-1.5 text-xs text-ink-muted">
        Saved to Cloudflare R2 · event_gallery · max {IMAGE_UPLOAD_MAX_MB} MB per image · large images compressed automatically
      </p>
    </div>
  );
}
